import React, { useMemo, useState, useCallback } from "react";
import { toast } from "react-toastify";
import { auth, db, storage } from "../../base";
import { ref, listAll, getMetadata, deleteObject } from "firebase/storage";
import {
  arrayRemove,
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  updateDoc,
  orderBy,
  limit,
} from "firebase/firestore";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Modal,
  Nav,
  Pagination,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { Rating } from "@mui/material";
import { useTranslation } from "react-i18next";
import "./adminDashboard.css";

const ITEMS_PER_PAGE = 12;
const PLAN_NAMES = {
  standard_trial: "Free trial",
  free: "Free",
  my_project_plan: "My Project",
  standard_plan: "Standard",
  pro_plan: "Pro",
  "My Project": "My Project",
  Standard: "Standard",
  Pro: "Pro",
};

const getAccountType = (user = {}) => {
  const plan = user.planName || user.planType || user.currentPlan || "free";
  if (user.role === "mainAdmin" || user.role === "admin") return "Admin";
  if (user.standardTrialStatus === "active" || plan === "standard_trial") return "Free trial";
  if (["my_project_plan", "standard_plan", "pro_plan", "My Project", "Standard", "Pro"].includes(plan)) return PLAN_NAMES[plan];
  if (Number(user.extraProjectPurchased || 0) > 0) return "Purchased project";
  return PLAN_NAMES[plan] || "Free";
};

const isCurrentTrial = (user = {}) => {
  const endsAt = normalizeDate(user.standardTrialEndsAt);
  return user.standardTrialStatus === "active" && endsAt && endsAt.getTime() > Date.now();
};

const parseProjectJson = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;

  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch (error) {
      console.error("Failed to parse project json:", error);
    }
  }

  return null;
};

const resolveMediaSrc = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value;

  if (typeof value === "object") {
    if (typeof value.src === "string") return value.src;
    if (typeof value.url === "string") return value.url;
    if (typeof value.downloadURL === "string") return value.downloadURL;
    if (typeof value.fullPath === "string") return value.fullPath;
  }

  return null;
};

const resolveProjectPreview = (project) => {
  if (!project) return null;

  if (project.imageUrls?.resized) return project.imageUrls.resized;
  if (project.imageUrls?.originalTreated) return project.imageUrls.originalTreated;
  if (project.imageURL) return project.imageURL;
  if (project.canvasProjectImage && project.canvasProjectImage !== "MISSING_IMAGE_URL") {
    return project.canvasProjectImage;
  }

  const projectJson = parseProjectJson(project.projectJson);
  if (projectJson?.canvasProjectImage && projectJson.canvasProjectImage !== "MISSING_IMAGE_URL") {
    return projectJson.canvasProjectImage;
  }
  if (projectJson?.backgroundImageProps?.src) return projectJson.backgroundImageProps.src;
  if (projectJson?.backgroundImage?.src) return projectJson.backgroundImage.src;
  if (typeof projectJson?.backgroundImage === "string") return projectJson.backgroundImage;

  return null;
};

const resolveGeneratedPreviews = (project) => {
  if (!Array.isArray(project?.generatedImages)) return [];

  return project.generatedImages
    .map((entry, index) => {
      const src = resolveMediaSrc(entry?.url || entry);
      if (!src) return null;

      return {
        id: entry?.id || entry?.createdAt || `${project?.id || "project"}-generated-${index}`,
        src,
        source: entry?.source || "generated",
      };
    })
    .filter(Boolean);
};

const normalizeDate = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDate = (value) => {
  const date = normalizeDate(value);
  return date ? date.toLocaleString() : "N/A";
};

const getProjectSortDate = (project) => {
  return (
    normalizeDate(project?.savedTimestamp) ||
    normalizeDate(project?.creationDate) ||
    normalizeDate(project?.updatedAt) ||
    new Date(0)
  );
};

const AdminDashboard = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("users");
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewUsers, setOverviewUsers] = useState([]);
  const [overviewProjects, setOverviewProjects] = useState([]);
  const [overviewEvents, setOverviewEvents] = useState([]);
  const [overviewError, setOverviewError] = useState("");
  const [overviewLoadStatus, setOverviewLoadStatus] = useState({ users: "idle", projects: "idle", events: "idle" });
  const [overviewRange, setOverviewRange] = useState("30");

  const [searchMode, setSearchMode] = useState("email");
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userProjects, setUserProjects] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [emailStatus, setEmailStatus] = useState(null);
  const [emailStatusLoading, setEmailStatusLoading] = useState(false);
  const [selectedProjectPreview, setSelectedProjectPreview] = useState(null);

  const [showImagesModal, setShowImagesModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [imageList, setImageList] = useState([]);
  const [storageLoading, setStorageLoading] = useState(false);

  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackList, setFeedbackList] = useState([]);
  const [allFeedbackData, setAllFeedbackData] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFeedbackIds, setSelectedFeedbackIds] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [feedbackToDelete, setFeedbackToDelete] = useState(null);

  const threeMonthsAgo = new Date();
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

  const paginatedFeedbackSummary = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return {
      startIndex,
      endIndex,
      totalPages: Math.ceil(allFeedbackData.length / ITEMS_PER_PAGE),
      pageData: allFeedbackData.slice(startIndex, endIndex),
    };
  }, [allFeedbackData, currentPage]);

  const fetchOverview = useCallback(async () => {
    setOverviewLoading(true);
    setOverviewError("");
    try {
      const results = await Promise.allSettled([
        getDocs(collection(db, "users")),
        getDocs(collection(db, "Projects")),
        getDocs(query(collection(db, "adminAnalyticsEvents"), orderBy("createdAt", "desc"), limit(1000))),
      ]);
      const [usersResult, projectsResult, eventsResult] = results;
      const statuses = {
        users: usersResult.status === "fulfilled" ? "loaded" : usersResult.reason?.code || "error",
        projects: projectsResult.status === "fulfilled" ? "loaded" : projectsResult.reason?.code || "error",
        events: eventsResult.status === "fulfilled" ? "loaded" : eventsResult.reason?.code || "error",
      };
      setOverviewLoadStatus(statuses);
      if (usersResult.status === "rejected") console.error("Admin overview users read failed:", usersResult.reason);
      if (projectsResult.status === "rejected") console.error("Admin overview projects read failed:", projectsResult.reason);
      if (eventsResult.status === "rejected") console.error("Admin overview events read failed:", eventsResult.reason);
      if (usersResult.status === "fulfilled") setOverviewUsers(usersResult.value.docs.map((item) => ({ id: item.id, ...item.data() })));
      if (projectsResult.status === "fulfilled") setOverviewProjects(projectsResult.value.docs.map((item) => ({ id: item.id, ...item.data() })));
      if (eventsResult.status === "fulfilled") setOverviewEvents(eventsResult.value.docs.map((item) => ({ id: item.id, ...item.data() })));
      const rejected = results.map((result, index) => result.status === "rejected" ? ["users", "projects", "events"][index] : null).filter(Boolean);
      if (rejected.length) setOverviewError(`Unable to load: ${rejected.join(", ")}. Check the Firestore rules for this signed-in admin. (${rejected.map((key) => statuses[key]).join(", ")})`);
    } catch (error) {
      console.error("Error loading admin overview:", error);
      setOverviewError(error.message || "Could not load overview data.");
    } finally {
      setOverviewLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (activeTab === "overview") fetchOverview();
  }, [activeTab, fetchOverview]);

  const overview = useMemo(() => {
    const now = Date.now();
    const since = overviewRange === "all" ? 0 : now - Number(overviewRange) * 24 * 60 * 60 * 1000;
    const recent = (date) => {
      const normalized = normalizeDate(date);
      return Boolean(normalized && normalized.getTime() >= since);
    };
    const accounts = overviewUsers.filter((user) => user.role !== "guest");
    const types = accounts.reduce((counts, user) => {
      const type = getAccountType(user);
      counts[type] = (counts[type] || 0) + 1;
      return counts;
    }, {});
    const activeTrials = accounts.filter(isCurrentTrial);
    const trialUsers = accounts.filter((user) => user.standardTrialStartedAt || user.standardTrialStatus === "pending_verification");
    const projectsByUser = overviewProjects.reduce((index, project) => {
      if (!index[project.userId]) index[project.userId] = [];
      index[project.userId].push(project);
      return index;
    }, {});
    const events = overviewEvents.filter((event) => recent(event.createdAt));
    const eventCount = (name) => events.filter((event) => event.eventName === name).length;
    const sourceCounts = events.filter((event) => event.eventName === "account_created" || event.eventName === "trial_requested")
      .reduce((counts, event) => {
        const source = event.acquisition?.utm_source || event.acquisition?.referrer || event.source || "unknown";
        counts[source] = (counts[source] || 0) + 1;
        return counts;
      }, {});
    const activeTrialRows = activeTrials.map((user) => ({
      ...user,
      projects: projectsByUser[user.id] || [],
    })).sort((a, b) => (normalizeDate(a.standardTrialEndsAt)?.getTime() || Infinity) - (normalizeDate(b.standardTrialEndsAt)?.getTime() || Infinity));
    const recentEvents = events.sort((a, b) => (normalizeDate(b.createdAt)?.getTime() || 0) - (normalizeDate(a.createdAt)?.getTime() || 0)).slice(0, 100);
    return {
      accountCount: accounts.length,
      types,
      activeTrials: activeTrials.length,
      trialStarted: trialUsers.filter((user) => recent(user.standardTrialStartedAt) || recent(user.standardTrialRequestedAt)).length,
      projectsInRange: overviewProjects.filter((project) => recent(project.creationDate || project.createdAt)).length,
      accountsInRange: accounts.filter((user) => recent(user.createdAt || user.accountCreatedAt)).length,
      sourceCounts,
      activeTrialRows,
      recentEvents,
      eventCounts: {
        accountCreated: eventCount("account_created"),
        trialRequested: eventCount("trial_requested"),
        trialActivated: eventCount("trial_activated"),
        projectCreated: eventCount("project_created"),
        trialProjectCreated: eventCount("trial_project_created"),
      },
      trackedEventCount: events.length,
    };
  }, [overviewUsers, overviewProjects, overviewEvents, overviewRange]);

  const fetchProjectsForUser = async (user) => {
    if (!user?.id) {
      setUserProjects([]);
      return;
    }

    setProjectsLoading(true);
    try {
      const projectsSnapshot = await getDocs(
        query(collection(db, "Projects"), where("userId", "==", user.id))
      );

      const projectsData = projectsSnapshot.docs
        .map((projectDoc) => ({
          id: projectDoc.id,
          ...projectDoc.data(),
        }))
        .sort((a, b) => getProjectSortDate(b) - getProjectSortDate(a));

      setUserProjects(projectsData);
    } catch (error) {
      console.error("Error fetching projects for user:", error);
      toast.error("Error loading user projects.");
      setUserProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  };

  const handleSelectUser = async (user) => {
    setSelectedUser(user);
    setEmailStatus(null);
    await fetchProjectsForUser(user);
    await fetchUserEmailStatus(user.id);
  };

  const fetchUserEmailStatus = async (targetUserId, sync = false) => {
    if (!targetUserId) return null;

    const currentAuthUser = auth.currentUser;
    if (!currentAuthUser) {
      toast.error("You must be signed in to inspect email status.");
      return null;
    }

    setEmailStatusLoading(true);

    try {
      const token = await currentAuthUser.getIdToken();
      const response = await fetch("/.netlify/functions/admin-user-email-status", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          targetUserId,
          sync,
        }),
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || "Email status request failed.");
      }

      setEmailStatus(payload);

      if (sync) {
        const nextEmail = payload?.firestore?.exists ? payload.firestore.email : undefined;
        const nextEmailConfirmed = payload?.firestore?.exists
          ? payload.firestore.emailConfirmed
          : undefined;

        setSelectedUser((prev) =>
          prev && prev.id === targetUserId
            ? {
                ...prev,
                ...(typeof nextEmail !== "undefined" ? { email: nextEmail } : {}),
                ...(typeof nextEmailConfirmed !== "undefined"
                  ? { emailConfirmed: nextEmailConfirmed }
                  : {}),
              }
            : prev
        );

        setSearchResults((prev) =>
          prev.map((item) =>
            item.id === targetUserId
              ? {
                  ...item,
                  ...(typeof nextEmail !== "undefined" ? { email: nextEmail } : {}),
                  ...(typeof nextEmailConfirmed !== "undefined"
                    ? { emailConfirmed: nextEmailConfirmed }
                    : {}),
                }
              : item
          )
        );
      }

      return payload;
    } catch (error) {
      console.error("Error fetching email status:", error);
      toast.error(error.message || "Error loading email status.");
      return null;
    } finally {
      setEmailStatusLoading(false);
    }
  };

  const handleUserSearch = async () => {
    const term = searchValue.trim();
    if (!term) {
      toast.error("Enter an email address or user ID.");
      return;
    }

    setUsersLoading(true);
    setSelectedUser(null);
    setUserProjects([]);
    setSearchResults([]);
    setEmailStatus(null);

    try {
      let results = [];

      if (searchMode === "userId") {
        const userSnapshot = await getDoc(doc(db, "users", term));
        if (userSnapshot.exists()) {
          results = [{ id: userSnapshot.id, ...userSnapshot.data() }];
        }
      } else {
        const usersSnapshot = await getDocs(
          query(collection(db, "users"), where("email", "==", term))
        );

        results = usersSnapshot.docs.map((userDoc) => ({
          id: userDoc.id,
          ...userDoc.data(),
        }));
      }

      setSearchResults(results);

      if (!results.length) {
        toast.info("No users matched this search.");
        return;
      }

      await handleSelectUser(results[0]);
    } catch (error) {
      console.error("Error searching users:", error);
      toast.error("Error searching for users.");
    } finally {
      setUsersLoading(false);
    }
  };

  const deleteStorageObjectIfPresent = async (storagePathOrUrl) => {
    if (!storagePathOrUrl) return;

    try {
      await deleteObject(ref(storage, storagePathOrUrl));
    } catch (error) {
      if (error?.code !== "storage/object-not-found") {
        console.warn("Failed to delete storage object:", storagePathOrUrl, error);
      }
    }
  };

  const deleteSingleProject = async (project) => {
    if (!selectedUser?.id || !project?.id) return;

    const confirmed = window.confirm(
      `Delete project "${(project.projectName || "Untitled").replaceAll("_", " ")}" for ${selectedUser.email || selectedUser.id}?`
    );

    if (!confirmed) return;

    setActionLoading(`project-${project.id}`);

    try {
      const imageBaseName = project.imageName || `${project.projectName || project.id}_${selectedUser.id}`;

      await Promise.all([
        deleteStorageObjectIfPresent(`usersProjectsImages/${selectedUser.id}/canvasImages/${imageBaseName}`),
        deleteStorageObjectIfPresent(`usersProjectsImages/${selectedUser.id}/canvasImages/canvasImagesTreated/${imageBaseName}.png`),
        deleteStorageObjectIfPresent(`usersProjectsImages/${selectedUser.id}/canvasImages/canvasImagesResized/${imageBaseName}_resized.png`),
        deleteStorageObjectIfPresent(`usersProjectsImages/${selectedUser.id}/canvasImages/canvasImagesWebP/${imageBaseName}_webp.webp`),
      ]);

      const generatedImages = Array.isArray(project.generatedImages) ? project.generatedImages : [];
      await Promise.all(
        generatedImages.map((entry) => deleteStorageObjectIfPresent(entry?.url || entry))
      );

      await deleteDoc(doc(db, "Projects", project.id));

      try {
        const usersProjectsRef = doc(db, "usersProjects", project.id);
        const usersProjectsSnapshot = await getDoc(usersProjectsRef);
        if (usersProjectsSnapshot.exists()) {
          await deleteDoc(usersProjectsRef);
        }
      } catch (secondaryDeleteError) {
        console.warn("Failed to delete usersProjects mirror doc:", secondaryDeleteError);
      }

      try {
        await updateDoc(doc(db, "users", selectedUser.id), {
          projectIds: arrayRemove(project.id),
        });
      } catch (userUpdateError) {
        console.warn("Failed to remove project id from user doc:", userUpdateError);
      }

      const updatedProjects = userProjects.filter((item) => item.id !== project.id);
      setUserProjects(updatedProjects);
      setSearchResults((prev) =>
        prev.map((item) =>
          item.id === selectedUser.id
            ? {
                ...item,
                projectIds: Array.isArray(item.projectIds)
                  ? item.projectIds.filter((projectId) => projectId !== project.id)
                  : item.projectIds,
              }
            : item
        )
      );

      toast.success("Project deleted.");
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error("Error deleting project.");
    } finally {
      setActionLoading("");
    }
  };

  const runUserDeleteAction = async (deleteAuthAccount) => {
    if (!selectedUser?.id) return;

    const label = deleteAuthAccount ? "user account and all data" : "projects, storage, and related user data";
    const confirmed = window.confirm(
      `Delete ${label} for ${selectedUser.email || selectedUser.id}? This action cannot be undone.`
    );

    if (!confirmed) return;

    const currentAuthUser = auth.currentUser;
    if (!currentAuthUser) {
      toast.error("You must be signed in to perform this action.");
      return;
    }

    setActionLoading(deleteAuthAccount ? "delete-account" : "delete-data");

    try {
      const token = await currentAuthUser.getIdToken();
      const response = await fetch("/.netlify/functions/admin-delete-user", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          targetUserId: selectedUser.id,
          deleteAuthAccount,
        }),
      });

      const rawResponse = await response.text();
      let payload = null;

      try {
        payload = rawResponse ? JSON.parse(rawResponse) : null;
      } catch (parseError) {
        payload = { error: rawResponse || "Delete request failed." };
      }

      if (!response.ok) {
        throw new Error(payload?.error || "Delete request failed.");
      }

      toast.success(
        deleteAuthAccount
          ? "User account and data deleted."
          : "User projects and related data deleted."
      );

      if (deleteAuthAccount) {
        setSearchResults((prev) => prev.filter((item) => item.id !== selectedUser.id));
        setSelectedUser(null);
        setUserProjects([]);
      } else {
        setUserProjects([]);
        setSearchResults((prev) =>
          prev.map((item) =>
            item.id === selectedUser.id
              ? {
                  ...item,
                  projectIds: [],
                }
              : item
          )
        );
        setSelectedUser((prev) => (prev ? { ...prev, projectIds: [] } : prev));
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error(error.message || "Error deleting user.");
    } finally {
      setActionLoading("");
    }
  };

  const updateDisplayedFeedback = (allData, page) => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    setFeedbackList(allData.slice(startIndex, endIndex));
    setCurrentPage(page);
  };

  const fetchLatestFeedback = async () => {
    setFeedbackLoading(true);
    setFeedbackList([]);
    setCurrentPage(1);
    setSelectedFeedbackIds(new Set());

    try {
      const feedbackSnapshot = await getDocs(collectionGroup(db, "Feedback"));
      const allFeedback = feedbackSnapshot.docs
        .map((feedbackDoc) => {
          const data = feedbackDoc.data();
          const pathSegments = feedbackDoc.ref.path.split("/");

          return {
            id: feedbackDoc.id,
            userId: pathSegments[1],
            docRef: feedbackDoc.ref,
            ...data,
            timestamp: data.timestamp || null,
            formattedDate: data.timestamp ? new Date(data.timestamp).toLocaleString() : "No date",
          };
        })
        .sort((a, b) => {
          const dateA = a.timestamp ? new Date(a.timestamp) : new Date(0);
          const dateB = b.timestamp ? new Date(b.timestamp) : new Date(0);
          return dateB - dateA;
        });

      setAllFeedbackData(allFeedback);
      updateDisplayedFeedback(allFeedback, 1);
      setShowFeedbackModal(true);

      if (!allFeedback.length) {
        toast.info("No detection feedback found in the database.");
      }
    } catch (error) {
      console.error("Error fetching feedback:", error);
      toast.error("Error retrieving feedback data.");
    } finally {
      setFeedbackLoading(false);
    }
  };

  const deleteFeedbackEntry = async (feedback) => {
    try {
      await deleteDoc(feedback.docRef);
      const updatedAllData = allFeedbackData.filter((item) => item.id !== feedback.id);
      setAllFeedbackData(updatedAllData);
      updateDisplayedFeedback(
        updatedAllData,
        currentPage > Math.ceil(updatedAllData.length / ITEMS_PER_PAGE)
          ? Math.max(1, Math.ceil(updatedAllData.length / ITEMS_PER_PAGE))
          : currentPage
      );
      toast.success(`Feedback entry "${feedback.projectName || "Unnamed"}" deleted successfully.`);
    } catch (error) {
      console.error("Error deleting feedback:", error);
      toast.error("Error deleting feedback entry.");
    }
  };

  const handleFeedbackSelection = (feedbackId, isSelected) => {
    const nextSelection = new Set(selectedFeedbackIds);
    if (isSelected) {
      nextSelection.add(feedbackId);
    } else {
      nextSelection.delete(feedbackId);
    }
    setSelectedFeedbackIds(nextSelection);
  };

  const handleSelectAllFeedback = (isSelected) => {
    const nextSelection = new Set(selectedFeedbackIds);
    feedbackList.forEach((feedback) => {
      if (isSelected) nextSelection.add(feedback.id);
      else nextSelection.delete(feedback.id);
    });
    setSelectedFeedbackIds(nextSelection);
  };

  const deleteSelectedFeedback = async () => {
    try {
      const selectedItems = allFeedbackData.filter((item) => selectedFeedbackIds.has(item.id));
      await Promise.all(selectedItems.map((item) => deleteDoc(item.docRef)));

      const updatedAllData = allFeedbackData.filter((item) => !selectedFeedbackIds.has(item.id));
      setAllFeedbackData(updatedAllData);
      setSelectedFeedbackIds(new Set());
      updateDisplayedFeedback(updatedAllData, Math.min(currentPage, Math.max(1, Math.ceil(updatedAllData.length / ITEMS_PER_PAGE))));
      toast.success(`${selectedItems.length} feedback entries deleted successfully.`);
    } catch (error) {
      console.error("Error deleting selected feedback:", error);
      toast.error("Error deleting selected feedback entries.");
    } finally {
      setShowDeleteConfirm(false);
    }
  };

  const fetchOldImages = async () => {
    setStorageLoading(true);
    setImageList([]);

    try {
      const storageRef = ref(storage, "usersProjectsImages/");
      const userFolders = await listAll(storageRef);
      const allImages = [];

      for (const userFolder of userFolders.prefixes) {
        const mainCanvasFolder = `${userFolder.fullPath}/canvasImages`;
        const subfolders = ["canvasImagesResized", "canvasImagesTreated", "canvasImagesWebP"];

        const processFolder = async (folderPath) => {
          try {
            const folderRef = ref(storage, folderPath);
            const images = await listAll(folderRef);

            for (const imgRef of images.items) {
              try {
                const metadata = await getMetadata(imgRef);
                const updatedDate = metadata.updated ? new Date(metadata.updated) : null;

                if (!updatedDate || updatedDate >= threeMonthsAgo) continue;
                if (imgRef.name.toLowerCase().includes("duplex")) continue;

                allImages.push({
                  name: imgRef.name,
                  fullPath: imgRef.fullPath,
                  updated: updatedDate.toLocaleString(),
                  size: `${(metadata.size / 1024).toFixed(2)} KB`,
                });
              } catch (metadataError) {
                console.warn(`Could not fetch metadata for ${imgRef.fullPath}:`, metadataError);
              }
            }
          } catch (folderError) {
            console.warn(`Could not list images in ${folderPath}:`, folderError);
          }
        };

        await processFolder(mainCanvasFolder);

        for (const subfolderName of subfolders) {
          await processFolder(`${mainCanvasFolder}/${subfolderName}`);
        }
      }

      setImageList(allImages);
      setShowImagesModal(true);

      if (!allImages.length) {
        toast.info("No images older than 3 months found in storage.");
      }
    } catch (error) {
      console.error("Error fetching images:", error);
      toast.error("Error retrieving images from storage.");
    } finally {
      setStorageLoading(false);
    }
  };

  const confirmDeletion = async () => {
    setShowConfirmModal(false);
    setStorageLoading(true);

    try {
      for (const image of imageList) {
        await deleteObject(ref(storage, image.fullPath));
      }

      toast.success("All selected images have been deleted.");
      setImageList([]);
      setShowImagesModal(false);
    } catch (error) {
      console.error("Error deleting images:", error);
      toast.error("Error deleting selected images.");
    } finally {
      setStorageLoading(false);
    }
  };

  const selectedUserProjectCount = userProjects.length;
  const selectedUserProjectIdsCount = Array.isArray(selectedUser?.projectIds) ? selectedUser.projectIds.length : 0;
  const selectedUserHasActiveSubscription = Boolean(
    selectedUser?.subscriptionId &&
      !["canceled", "cancelled", "inactive"].includes(
        String(selectedUser?.subscriptionStatus || "").toLowerCase()
      )
  );

  return (
    <>
      <div className="admin-dashboard-shell">
        <Nav
          variant="pills"
          activeKey={activeTab}
          onSelect={(value) => value && setActiveTab(value)}
          className="admin-dashboard-tabs"
        >
          <Nav.Item><Nav.Link eventKey="overview">Overview</Nav.Link></Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="users">{t("Users")}</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="storage">{t("Storage")}</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="feedback">{t("Feedback")}</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="news">{t("News")}</Nav.Link>
          </Nav.Item>
        </Nav>

        {activeTab === "overview" && (
          <div className="admin-dashboard-panel admin-overview">
            <div className="admin-overview-toolbar">
              <div>
                <h2>App overview</h2>
                <p className="text-muted mb-0">Accounts, trials, project activity, and acquisition milestones.</p>
              </div>
              <div className="d-flex gap-2">
                <Form.Select value={overviewRange} onChange={(event) => setOverviewRange(event.target.value)} aria-label="Date range">
                  <option value="7">Last 7 days</option><option value="30">Last 30 days</option><option value="90">Last 90 days</option><option value="all">All time</option>
                </Form.Select>
                <Button variant="outline-primary" onClick={fetchOverview} disabled={overviewLoading}>{overviewLoading ? "Refreshing…" : "Refresh"}</Button>
              </div>
            </div>
            {overviewError && <Alert variant="danger">{overviewError}</Alert>}
            {overviewLoading && !overviewUsers.length ? <div className="text-center p-5"><Spinner animation="border" /></div> : <>
              <div className="admin-overview-metrics">
                <Card><Card.Body><span>Total accounts</span><strong>{overview.accountCount}</strong></Card.Body></Card>
                <Card><Card.Body><span>Active free trials</span><strong>{overview.activeTrials}</strong></Card.Body></Card>
                <Card><Card.Body><span>Trials started in range</span><strong>{overview.trialStarted}</strong></Card.Body></Card>
                <Card><Card.Body><span>Accounts created in range</span><strong>{overview.accountsInRange}</strong></Card.Body></Card>
                <Card><Card.Body><span>Projects created in range</span><strong>{overview.projectsInRange}</strong></Card.Body></Card>
              </div>
              <Row className="g-3 mt-1">
                <Col lg={4}><Card className="admin-panel-card h-100"><Card.Body><h3>Accounts by type</h3>{Object.entries(overview.types).sort((a,b)=>b[1]-a[1]).map(([name,count])=><div className="admin-overview-breakdown" key={name}><span>{name}</span><strong>{count}</strong></div>)}</Card.Body></Card></Col>
                <Col lg={8}><Card className="admin-panel-card h-100"><Card.Body><h3>Acquisition sources</h3><p className="small text-muted">Counted from tracked account and trial events in the selected range. Older activity may not include a source.</p>{Object.entries(overview.sourceCounts).sort((a,b)=>b[1]-a[1]).map(([name,count])=><div className="admin-overview-breakdown" key={name}><span>{name}</span><strong>{count}</strong></div>)}{!Object.keys(overview.sourceCounts).length && <span className="text-muted">No tracked sources yet.</span>}</Card.Body></Card></Col>
              </Row>
              <Card className="admin-panel-card mt-3"><Card.Body><h3>Users and projects on active trial</h3><div className="table-responsive"><Table hover className="align-middle mb-0"><thead><tr><th>Email</th><th>Trial started</th><th>Ends</th><th>Projects</th><th>Account</th><th>Acquisition</th></tr></thead><tbody>{overview.activeTrialRows.map((user)=><tr key={user.id}><td>{user.email || "No email"}<div className="small text-muted">{user.id}</div></td><td>{formatDate(user.standardTrialStartedAt)}</td><td>{formatDate(user.standardTrialEndsAt)}</td><td>{user.projects.length ? user.projects.map((project)=><div key={project.id}>{(project.projectName || "Untitled").replaceAll("_", " ")} <small className="text-muted">{formatDate(project.creationDate)}</small></div>) : <span className="text-muted">No project yet</span>}</td><td>{getAccountType(user)}</td><td>{user.acquisition?.utm_source || user.acquisition?.referrer || user.standardTrialSource || user.signupSource || "Unknown"}</td></tr>)}</tbody></Table></div>{!overview.activeTrialRows.length && <p className="text-muted mb-0">No active trial accounts.</p>}</Card.Body></Card>
              <Card className="admin-panel-card mt-3"><Card.Body><h3>Recent account journey events</h3><div className="admin-event-summary"><span>Accounts <strong>{overview.eventCounts.accountCreated}</strong></span><span>Trial requests <strong>{overview.eventCounts.trialRequested}</strong></span><span>Trials activated <strong>{overview.eventCounts.trialActivated}</strong></span><span>Projects <strong>{overview.eventCounts.projectCreated}</strong></span><span>Trial projects <strong>{overview.eventCounts.trialProjectCreated}</strong></span></div><div className="table-responsive"><Table hover className="align-middle mb-0"><thead><tr><th>When</th><th>Event</th><th>Email / user</th><th>Source</th><th>Project</th></tr></thead><tbody>{overview.recentEvents.map((event)=><tr key={event.id}><td>{formatDate(event.createdAt)}</td><td>{event.eventName?.replaceAll("_", " ") || "Event"}</td><td>{event.email || event.userId || "Anonymous"}</td><td>{event.source || "Unknown"}</td><td>{event.projectName || "—"}</td></tr>)}</tbody></Table></div>{!overview.recentEvents.length && <p className="text-muted mb-0">No first-party journey events have been recorded yet.</p>}<p className="small text-muted mt-2 mb-0">First-party event history available: {overview.trackedEventCount} events (up to the latest 1,000 loaded).</p></Card.Body></Card>
            </>}
          </div>
        )}

        {activeTab === "users" && (
          <div className="admin-dashboard-panel">
            <div className="d-flex flex-column gap-4">
              <div>
                <Card className="admin-panel-card">
                  <Card.Body>
                    <Card.Title>{t("Find User")}</Card.Title>
                    <Card.Text className="text-muted">
                      {t("Search by exact email or exact Firebase user ID, then inspect and manage that user's projects.")}
                    </Card.Text>

                    <InputGroup className="mb-3 admin-user-search-group">
                      <Form.Select
                        value={searchMode}
                        onChange={(event) => setSearchMode(event.target.value)}
                        style={{ maxWidth: "130px" }}
                      >
                        <option value="email">{t("Email")}</option>
                        <option value="userId">{t("User ID")}</option>
                      </Form.Select>
                      <Form.Control
                        value={searchValue}
                        onChange={(event) => setSearchValue(event.target.value)}
                        placeholder={searchMode === "email" ? "user@example.com" : "Firebase UID"}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            handleUserSearch();
                          }
                        }}
                      />
                      <Button
                        variant="primary"
                        className="admin-search-button"
                        onClick={handleUserSearch}
                        disabled={usersLoading}
                      >
                        {usersLoading ? <Spinner size="sm" animation="border" /> : "Search"}
                      </Button>
                    </InputGroup>

                    <div className="admin-search-results">
                      {searchResults.length === 0 ? (
                        <div className="text-muted small">{t("No selected user yet.")}</div>
                      ) : (
                        searchResults.map((user) => (
                          <button
                            key={user.id}
                            type="button"
                            className={`admin-user-result ${selectedUser?.id === user.id ? "is-active" : ""}`}
                            onClick={() => handleSelectUser(user)}
                          >
                            <div className="admin-user-result-main">
                              <strong>{user.email || t("No email")}</strong>
                              <div className="small text-muted">{user.id}</div>
                            </div>
                            <Badge bg={user.role === "mainAdmin" ? "danger" : "secondary"}>
                              {user.role || "user"}
                            </Badge>
                          </button>
                        ))
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </div>

              <div>
                <Card className="admin-panel-card">
                  <Card.Body>
                    <div className="admin-user-header">
                      <div>
                        <Card.Title>{t("User Overview")}</Card.Title>
                        <Card.Text className="text-muted">
                          {t("Review project data before taking destructive actions.")}
                        </Card.Text>
                      </div>

                      {selectedUser && (
                        <Button
                          variant="outline-secondary"
                          onClick={() => fetchProjectsForUser(selectedUser)}
                          disabled={projectsLoading}
                        >
                          {projectsLoading ? <Spinner size="sm" animation="border" /> : t("Refresh Projects")}
                        </Button>
                      )}
                    </div>

                    {!selectedUser ? (
                      <Alert variant="light" className="mb-0">
                        {t("Search for a user to inspect projects, images, and account data.")}
                      </Alert>
                    ) : (
                      <>
                        {selectedUserHasActiveSubscription && (
                          <Alert variant="warning">
                            {t("This user appears to have an active subscription. Account deletion is blocked until billing is handled manually.")}
                          </Alert>
                        )}

                        <div className="admin-user-summary">
                          <div>
                            <span className="admin-summary-label">{t("Email")}</span>
                            <div>{selectedUser.email || "N/A"}</div>
                          </div>
                          <div>
                            <span className="admin-summary-label">{t("Role")}</span>
                            <div>{selectedUser.role || "user"}</div>
                          </div>
                          <div>
                            <span className="admin-summary-label">{t("Plan")}</span>
                            <div>{selectedUser.planName || selectedUser.currentPlan || selectedUser.planType || "N/A"}</div>
                          </div>
                          <div>
                            <span className="admin-summary-label">{t("Subscription")}</span>
                            <div>{selectedUser.subscriptionStatus || "N/A"}</div>
                          </div>
                          <div>
                            <span className="admin-summary-label">{t("Project Docs")}</span>
                            <div>{selectedUserProjectCount}</div>
                          </div>
                          <div>
                            <span className="admin-summary-label">{t("projectIds Length")}</span>
                            <div>{selectedUserProjectIdsCount}</div>
                          </div>
                        </div>

                        <Card className="mt-3">
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                              <div>
                                <div className="fw-semibold">Email Verification Status</div>
                                <div className="text-muted small">
                                  Compare Firebase Auth with Firestore and sync `emailConfirmed` from Auth when needed.
                                </div>
                              </div>
                              <div className="d-flex gap-2">
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => fetchUserEmailStatus(selectedUser.id, false)}
                                  disabled={emailStatusLoading}
                                >
                                  {emailStatusLoading ? <Spinner size="sm" animation="border" /> : "Inspect"}
                                </Button>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => fetchUserEmailStatus(selectedUser.id, true)}
                                  disabled={emailStatusLoading}
                                >
                                  {emailStatusLoading ? <Spinner size="sm" animation="border" /> : "Sync from Auth"}
                                </Button>
                              </div>
                            </div>

                            {!emailStatus ? (
                              <div className="text-muted small mt-3">
                                No email verification data loaded yet.
                              </div>
                            ) : (
                              <div className="admin-user-summary mt-3">
                                <div>
                                  <span className="admin-summary-label">Auth Email</span>
                                  <div>{emailStatus.auth?.email || "N/A"}</div>
                                </div>
                                <div>
                                  <span className="admin-summary-label">Auth Verified</span>
                                  <div>{emailStatus.auth?.exists ? String(emailStatus.auth?.emailVerified) : "Auth user missing"}</div>
                                </div>
                                <div>
                                  <span className="admin-summary-label">Firestore Email</span>
                                  <div>{emailStatus.firestore?.email || "N/A"}</div>
                                </div>
                                <div>
                                  <span className="admin-summary-label">Firestore emailConfirmed</span>
                                  <div>{emailStatus.firestore?.exists ? String(emailStatus.firestore?.emailConfirmed) : "User doc missing"}</div>
                                </div>
                                <div>
                                  <span className="admin-summary-label">In Sync</span>
                                  <div>{String(Boolean(emailStatus.isInSync))}</div>
                                </div>
                                <div>
                                  <span className="admin-summary-label">Last Action</span>
                                  <div>{emailStatus.syncPerformed ? "Sync applied" : "Inspect only"}</div>
                                </div>
                              </div>
                            )}
                          </Card.Body>
                        </Card>

                        <div className="admin-danger-zone">
                          <div className="admin-danger-copy">
                            <strong>{t("Danger Zone")}</strong>
                            <div className="text-muted small">
                              {t("Delete data only after reviewing the project cards below.")}
                            </div>
                          </div>
                          <div className="admin-danger-actions">
                            <Button
                              variant="outline-danger"
                              disabled={actionLoading === "delete-data"}
                              onClick={() => runUserDeleteAction(false)}
                            >
                              {actionLoading === "delete-data" ? t("Deleting...") : t("Delete Projects + Data")}
                            </Button>
                            <Button
                              variant="danger"
                              disabled={selectedUserHasActiveSubscription || actionLoading === "delete-account"}
                              onClick={() => runUserDeleteAction(true)}
                            >
                              {actionLoading === "delete-account" ? t("Deleting...") : t("Delete User Account + Data")}
                            </Button>
                          </div>
                        </div>

                        <div className="admin-projects-section">
                          <div className="admin-projects-header">
                            <h5 className="mb-0">{t("Projects")}</h5>
                            <Badge bg="dark">{selectedUserProjectCount}</Badge>
                          </div>

                          {projectsLoading ? (
                            <div className="py-4 text-center">
                              <Spinner animation="border" />
                            </div>
                          ) : userProjects.length === 0 ? (
                            <Alert variant="light" className="mb-0">
                              {t("No projects found for this user.")}
                            </Alert>
                          ) : (
                            <Row className="g-3">
                              {userProjects.map((project) => {
                                const preview = resolveProjectPreview(project);
                                const generatedPreviews = resolveGeneratedPreviews(project);
                                const displayName = (project.projectName || "Untitled").replaceAll("_", " ");

                                return (
                                  <Col md={6} xl={4} key={project.id}>
                                    <Card className="admin-project-card h-100">
                                      {preview ? (
                                        <button
                                          type="button"
                                          className="admin-project-image-button"
                                          onClick={() =>
                                            setSelectedProjectPreview({
                                              name: displayName,
                                              src: preview,
                                            })
                                          }
                                        >
                                          <Card.Img
                                            variant="top"
                                            src={preview}
                                            alt={displayName}
                                            className="admin-project-image"
                                          />
                                        </button>
                                      ) : (
                                        <div className="admin-project-image admin-project-image-placeholder">
                                          {t("No preview")}
                                        </div>
                                      )}

                                      <Card.Body>
                                        <Card.Title className="h6">{displayName}</Card.Title>
                                        <div className="small text-muted mb-2">{project.id}</div>
                                        <div className="small">
                                          <div><strong>{t("Created")}:</strong> {formatDate(project.creationDate)}</div>
                                          <div><strong>{t("Saved")}:</strong> {formatDate(project.savedTimestamp)}</div>
                                          <div><strong>{t("Image Name")}:</strong> {project.imageName || "N/A"}</div>
                                          <div><strong>{t("Generated Images")}:</strong> {generatedPreviews.length}</div>
                                        </div>

                                        {generatedPreviews.length > 0 && (
                                          <div className="admin-generated-section">
                                            <div className="admin-generated-label">{t("Generated Images")}</div>
                                            <div className="admin-generated-grid">
                                              {generatedPreviews.map((generatedPreview, generatedIndex) => (
                                                <button
                                                  key={generatedPreview.id}
                                                  type="button"
                                                  className="admin-generated-thumb-button"
                                                  onClick={() =>
                                                    setSelectedProjectPreview({
                                                      name: `${displayName} - ${t("Generated")} ${generatedIndex + 1}`,
                                                      src: generatedPreview.src,
                                                    })
                                                  }
                                                >
                                                  <img
                                                    src={generatedPreview.src}
                                                    alt={`${displayName} generated ${generatedIndex + 1}`}
                                                    className="admin-generated-thumb"
                                                  />
                                                </button>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </Card.Body>
                                      <Card.Footer className="bg-white border-0 pt-0">
                                        <Button
                                          variant="outline-danger"
                                          size="sm"
                                          className="w-100"
                                          disabled={actionLoading === `project-${project.id}`}
                                          onClick={() => deleteSingleProject(project)}
                                        >
                                          {actionLoading === `project-${project.id}` ? t("Deleting...") : t("Delete Project")}
                                        </Button>
                                      </Card.Footer>
                                    </Card>
                                  </Col>
                                );
                              })}
                            </Row>
                          )}
                        </div>
                      </>
                    )}
                  </Card.Body>
                </Card>
              </div>
            </div>
          </div>
        )}

        {activeTab === "storage" && (
          <div className="admin-dashboard-panel">
            <Card className="admin-panel-card">
              <Card.Body>
                <Card.Title>{t("Storage Maintenance")}</Card.Title>
                <Card.Text className="text-muted">
                  {t("Inspect and optionally remove old images across user storage.")}
                </Card.Text>
                <Button onClick={fetchOldImages} variant="info" disabled={storageLoading}>
                  {storageLoading ? t("Loading...") : t("Find and Delete Images Older Than 3 Months")}
                </Button>
              </Card.Body>
            </Card>
          </div>
        )}

        {activeTab === "feedback" && (
          <div className="admin-dashboard-panel">
            <Card className="admin-panel-card">
              <Card.Body>
                <Card.Title>{t("Detection Feedback")}</Card.Title>
                <Card.Text className="text-muted">
                  {t("Review and delete AI detection feedback entries.")}
                </Card.Text>
                <Button onClick={fetchLatestFeedback} variant="success" disabled={feedbackLoading}>
                  {feedbackLoading ? t("Loading...") : t("View Latest Detection Feedback")}
                </Button>
              </Card.Body>
            </Card>
          </div>
        )}

        {activeTab === "news" && (
          <div className="admin-dashboard-panel">
            <Card className="admin-panel-card">
              <Card.Body>
                <Card.Title>{t("News Management")}</Card.Title>
                <Card.Text className="text-muted">
                  {t("Open the existing news composer without leaving the admin control panel.")}
                </Card.Text>
                <Button
                  variant="success"
                  onClick={() => document.querySelector("[data-news-admin-trigger]")?.click()}
                >
                  {t("Open News Management Tool")}
                </Button>
              </Card.Body>
            </Card>
          </div>
        )}
      </div>

      <Modal show={showImagesModal} onHide={() => setShowImagesModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t("Images Older Than 3 Months")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {storageLoading ? (
            <p>{t("Loading...")}</p>
          ) : imageList.length > 0 ? (
            <>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>{t("Image Name")}</th>
                    <th>{t("Last Updated")}</th>
                    <th>{t("Size")}</th>
                  </tr>
                </thead>
                <tbody>
                  {imageList.map((image) => (
                    <tr key={image.fullPath}>
                      <td>{image.name}</td>
                      <td>{image.updated}</td>
                      <td>{image.size}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Button variant="danger" onClick={() => setShowConfirmModal(true)}>
                {t("Confirm Delete All")}
              </Button>
            </>
          ) : (
            <p>{t("No old images found.")}</p>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t("Confirm Deletion")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t("Are you sure you want to delete these images? This action cannot be undone.")}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            {t("Cancel")}
          </Button>
          <Button variant="danger" onClick={confirmDeletion}>
            {t("Delete")}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showFeedbackModal} onHide={() => setShowFeedbackModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            {t("Latest Detection Feedback")}
            {allFeedbackData.length > 0 && (
              <span className="text-muted ms-2">({allFeedbackData.length} total entries)</span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "80vh", overflowY: "auto" }}>
          {feedbackLoading ? (
            <p>{t("Loading...")}</p>
          ) : feedbackList.length > 0 ? (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <Form.Check
                    type="checkbox"
                    label="Select all on this page"
                    checked={feedbackList.length > 0 && feedbackList.every((item) => selectedFeedbackIds.has(item.id))}
                    onChange={(event) => handleSelectAllFeedback(event.target.checked)}
                    className="d-inline-block me-3"
                  />
                  {selectedFeedbackIds.size > 0 && (
                    <Button variant="danger" size="sm" onClick={() => setShowDeleteConfirm(true)}>
                      Delete Selected ({selectedFeedbackIds.size})
                    </Button>
                  )}
                </div>
                <small className="text-muted">
                  Showing {paginatedFeedbackSummary.startIndex + 1}-{Math.min(paginatedFeedbackSummary.endIndex, allFeedbackData.length)} of {allFeedbackData.length}
                </small>
              </div>

              <Row>
                {feedbackList.map((feedback) => (
                  <Col md={6} lg={4} key={`${feedback.userId}-${feedback.id}`} className="mb-4">
                    <Card
                      style={{ height: "100%" }}
                      border={feedback.rating >= 4 ? "success" : feedback.rating <= 2 ? "danger" : "warning"}
                    >
                      <Card.Header className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          <Form.Check
                            type="checkbox"
                            checked={selectedFeedbackIds.has(feedback.id)}
                            onChange={(event) => handleFeedbackSelection(feedback.id, event.target.checked)}
                            className="me-2"
                            onClick={(event) => event.stopPropagation()}
                          />
                          <small className="text-muted">{feedback.formattedDate}</small>
                        </div>
                        <div className="d-flex align-items-center">
                          <Rating value={feedback.rating || 0} size="small" readOnly className="me-2" />
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={(event) => {
                              event.stopPropagation();
                              setFeedbackToDelete(feedback);
                            }}
                            title="Delete this feedback"
                          >
                            ×
                          </Button>
                        </div>
                      </Card.Header>
                      {(resolveMediaSrc(feedback.projectImageUrl) || resolveMediaSrc(feedback.detectionImage)) && (
                        <Card.Img
                          variant="top"
                          src={resolveMediaSrc(feedback.projectImageUrl) || resolveMediaSrc(feedback.detectionImage)}
                          style={{ height: "150px", objectFit: "cover", cursor: "pointer" }}
                          alt="Feedback preview"
                          onClick={() => setSelectedFeedback(feedback)}
                        />
                      )}
                      <Card.Body style={{ cursor: "pointer" }} onClick={() => setSelectedFeedback(feedback)}>
                        <Card.Title className="h6">{feedback.projectName || "Unnamed Project"}</Card.Title>
                        <Card.Text className="small">
                          <strong>User:</strong> {feedback.userId.substring(0, 8)}...
                          <br />
                          {feedback.notes && (
                            <span>
                              <strong>Notes:</strong> {feedback.notes.substring(0, 50)}
                              {feedback.notes.length > 50 && "..."}
                            </span>
                          )}
                        </Card.Text>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>

              {paginatedFeedbackSummary.totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <Pagination>
                    <Pagination.First onClick={() => updateDisplayedFeedback(allFeedbackData, 1)} disabled={currentPage === 1} />
                    <Pagination.Prev onClick={() => updateDisplayedFeedback(allFeedbackData, currentPage - 1)} disabled={currentPage === 1} />
                    {Array.from({ length: paginatedFeedbackSummary.totalPages }, (_, index) => index + 1)
                      .filter((page) => {
                        const min = Math.max(1, currentPage - 2);
                        const max = Math.min(paginatedFeedbackSummary.totalPages, currentPage + 2);
                        return page >= min && page <= max;
                      })
                      .map((page) => (
                        <Pagination.Item
                          key={page}
                          active={page === currentPage}
                          onClick={() => updateDisplayedFeedback(allFeedbackData, page)}
                        >
                          {page}
                        </Pagination.Item>
                      ))}
                    <Pagination.Next
                      onClick={() => updateDisplayedFeedback(allFeedbackData, currentPage + 1)}
                      disabled={currentPage === paginatedFeedbackSummary.totalPages}
                    />
                    <Pagination.Last
                      onClick={() => updateDisplayedFeedback(allFeedbackData, paginatedFeedbackSummary.totalPages)}
                      disabled={currentPage === paginatedFeedbackSummary.totalPages}
                    />
                  </Pagination>
                </div>
              )}
            </>
          ) : (
            <p>{t("No feedback found.")}</p>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={selectedFeedback !== null} onHide={() => setSelectedFeedback(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t("Feedback Details")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedFeedback && (
            <div>
              <Row className="mb-3">
                <Col>
                  <h5>{selectedFeedback.projectName || "Unnamed Project"}</h5>
                  <p><strong>Date:</strong> {selectedFeedback.formattedDate}</p>
                  <p><strong>User ID:</strong> {selectedFeedback.userId}</p>
                  <p><strong>Rating:</strong> <Rating value={selectedFeedback.rating || 0} readOnly /></p>
                </Col>
              </Row>

              {selectedFeedback.notes && (
                <Row className="mb-3">
                  <Col>
                    <h6>Notes:</h6>
                    <p style={{ background: "#f8f9fa", padding: "10px", borderRadius: "5px" }}>
                      {selectedFeedback.notes}
                    </p>
                  </Col>
                </Row>
              )}

              <Row>
                <Col md={6}>
                  <h6>Original Image:</h6>
                  {resolveMediaSrc(selectedFeedback.projectImageUrl) ? (
                    <img
                      src={resolveMediaSrc(selectedFeedback.projectImageUrl)}
                      alt="Original project"
                      style={{ width: "100%", maxHeight: "300px", objectFit: "contain" }}
                    />
                  ) : resolveMediaSrc(selectedFeedback.detectionImage) ? (
                    <img
                      src={resolveMediaSrc(selectedFeedback.detectionImage)}
                      alt="Detection"
                      style={{ width: "100%", maxHeight: "300px", objectFit: "contain" }}
                    />
                  ) : (
                    <div className="text-muted">No image available</div>
                  )}
                </Col>
                <Col md={6}>
                  <h6>After Detection:</h6>
                  {resolveMediaSrc(selectedFeedback.detectionImage) ? (
                    <img
                      src={resolveMediaSrc(selectedFeedback.detectionImage)}
                      alt="After detection"
                      style={{ width: "100%", maxHeight: "300px", objectFit: "contain" }}
                    />
                  ) : resolveMediaSrc(selectedFeedback.projectImageUrl) ? (
                    <img
                      src={resolveMediaSrc(selectedFeedback.projectImageUrl)}
                      alt="Original"
                      style={{ width: "100%", maxHeight: "300px", objectFit: "contain" }}
                    />
                  ) : (
                    <div className="text-muted">No image available</div>
                  )}
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSelectedFeedback(null)}>
            {t("Close")}
          </Button>
          {selectedFeedback && (
            <Button
              variant="danger"
              onClick={() => {
                setSelectedFeedback(null);
                setFeedbackToDelete(selectedFeedback);
              }}
            >
              {t("Delete")}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <Modal show={feedbackToDelete !== null} onHide={() => setFeedbackToDelete(null)}>
        <Modal.Header closeButton>
          <Modal.Title>{t("Confirm Deletion")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {feedbackToDelete && (
            <p>
              Are you sure you want to delete the feedback for "{feedbackToDelete.projectName || "Unnamed Project"}"
              {" "}from user {feedbackToDelete.userId.substring(0, 8)}...?
              <br />
              <br />
              <strong>This action cannot be undone.</strong>
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setFeedbackToDelete(null)}>
            {t("Cancel")}
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              if (feedbackToDelete) {
                deleteFeedbackEntry(feedbackToDelete);
                setFeedbackToDelete(null);
              }
            }}
          >
            {t("Delete")}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t("Confirm Bulk Deletion")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete {selectedFeedbackIds.size} selected feedback entries?
            <br />
            <br />
            <strong>This action cannot be undone.</strong>
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
            {t("Cancel")}
          </Button>
          <Button variant="danger" onClick={deleteSelectedFeedback}>
            Delete {selectedFeedbackIds.size} Entries
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={selectedProjectPreview !== null} onHide={() => setSelectedProjectPreview(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{selectedProjectPreview?.name || t("Project Preview")}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {selectedProjectPreview?.src ? (
            <img
              src={selectedProjectPreview.src}
              alt={selectedProjectPreview.name || "Project preview"}
              className="admin-project-preview-modal-image"
            />
          ) : (
            <div className="text-muted">{t("No preview image available.")}</div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AdminDashboard;
